
import type { PropType } from 'vue'
import type { Pagination } from '../../models'
import { defineComponent } from 'vue'
import { capitalizeSentence } from '../../filters/capitalize-sentence.filter'
import { UiConfirm, UiRadioGroup, UiTooltip } from '../keen-ui'
import { EpbButton } from '../shared/epb-button'
import { EpbSelect } from '../shared/epb-select'
import { EpbTextbox } from '../shared/epb-textbox'

export enum DownloadOptionEnum {
  CURRENT = 'current',
  ALL_ROWS = 'all rows',
  CUSTOM = 'custom',
}

export default defineComponent({
  name: 'PaginationBar',
  components: {
    EpbButton,
    EpbSelect,
    EpbTextbox,
    UiConfirm,
    UiRadioGroup,
    UiTooltip,
  },
  props: {
    pagination: Object as PropType<Pagination>,
    itemsPerPageOptions: {
      type: Array as PropType<Array<number>>,
      default() {
        return [this.pagination.itemsPerPage]
      },
    },
    lastPageDisabled: Boolean,
    downloadEnabled: Boolean,
    downloadItemsPerPageOptions: {
      type: Array as PropType<Array<number>>,
      default: () => [],
    },
    downloadDialogTitle: String,
    useAlternativePagination: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      DownloadOptionEnum,
      itemsPerPageString: '',
      buttonConfig: {
        type: 'void',
        color: 'default',
      },
      download: {
        selectedOption: DownloadOptionEnum.CURRENT,
        itemsPerPageString: '',
        start: 1,
      },
    }
  },
  computed: {
    disableNextPage(): boolean {
      return this.useAlternativePagination
        ? this.pagination.totalItems < this.pagination.itemsPerPage
        : this.pagination.page >= this.lastPage
    },
    pageInfoText(): string {
      const firstItemPage = Math.min(
        (this.pagination.page - 1) * this.pagination.itemsPerPage + 1,
        this.pagination.totalItems,
      )
      let pageInfoText = firstItemPage.toLocaleString(this.$epb.locale)
      const lastItemPage = Math.min(
        this.pagination.page * this.pagination.itemsPerPage,
        this.pagination.totalItems,
      )
      if (lastItemPage !== firstItemPage) {
        pageInfoText += ` - ${lastItemPage.toLocaleString(this.$epb.locale)}`
      }
      pageInfoText += ` ${
        this.$epb.messages.crud.pagination.ofTotal
      } ${this.pagination.totalItems.toLocaleString(this.$epb.locale)}`
      return this.useAlternativePagination ? '' : pageInfoText
    },
    lastPage(): number {
      return Math.ceil(this.pagination.totalItems / this.pagination.itemsPerPage)
    },
    downloadOptions(): any[] {
      return [
        {
          label: this.$epb.messages.crud.pagination.download.currentPage,
          value: DownloadOptionEnum.CURRENT,
        },
        {
          label: this.$epb.messages.crud.pagination.download.allRows,
          value: DownloadOptionEnum.ALL_ROWS,
        },
        {
          label: this.$epb.messages.crud.pagination.download.custom,
          value: DownloadOptionEnum.CUSTOM,
        },
      ]
    },
  },
  watch: {
    pagination: {
      handler(pagination: Pagination): void {
        this.itemsPerPageString = pagination.itemsPerPage.toString()
      },
      immediate: true,
    },
    itemsPerPageString(itemsPerPageString: string): void {
      this.updateItemsPerPage(Number(itemsPerPageString))
    },
  },
  methods: {
    updatePage(page: number): void {
      const pageIsValid = this.useAlternativePagination ? true : page >= 1 && page <= this.lastPage
      if (page !== this.pagination.page && pageIsValid) {
        this.$emit('paginationChange', {
          ...this.pagination,
          page,
        })
      }
    },
    updateItemsPerPage(itemsPerPage: number): void {
      if (itemsPerPage !== this.pagination.itemsPerPage) {
        this.$emit('paginationChange', {
          ...this.pagination,
          page: 1,
          itemsPerPage,
        })
      }
    },
    onDownload(): void {
      this.download.selectedOption = DownloadOptionEnum.CURRENT
      if (this.downloadItemsPerPageOptions.length) {
        if (!this.download.itemsPerPageString) {
          this.download.itemsPerPageString = this.downloadItemsPerPageOptions[0].toString()
        }
        this.validateDownloadStart()
        this.$refs.downloadDialog.open()
      } else {
        this.onConfirmDownload()
      }
    },
    validateDownloadStart(): void {
      if (this.download.start > this.pagination.totalItems) {
        this.download.start = this.pagination.totalItems
      }
      if (!this.download.start || this.download.start < 1) {
        this.download.start = 1
      }
    },
    onConfirmDownload(): void {
      const pagination: Pagination = {
        ...this.pagination,
      }
      if (this.download.selectedOption === DownloadOptionEnum.CUSTOM) {
        pagination.itemsPerPage = Number(this.download.itemsPerPageString)
        pagination.page =
          Math.floor((Number(this.download.start) - 1) / pagination.itemsPerPage) + 1
      } else if (this.download.selectedOption === DownloadOptionEnum.ALL_ROWS) {
        pagination.itemsPerPage = pagination.totalItems
        pagination.page = 1
      }
      this.$emit('download', pagination)
    },
    capitalizeSentence,
  },
})
